import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Modal from 'react-modal';

import {
  fetchProductReviewsWithMedia,
  filterReviewsByMediaType,
  hideReviewGalleryModal,
  navigateToReviewWithMedia,
  selectGalleryMedia,
  showReviewGalleryModal,
  sortMediaReviews,
  toggleShowGallery
} from 'actions/reviews';
import { upvoteReview } from 'actions/productDetail';
import { trackEvent } from 'helpers/analytics';
import { Clear } from 'components/icons';
import Review from 'components/reviews/Review';
import ReviewGalleryImage from 'components/reviews/ReviewGalleryImage';
import ReviewGalleryMediaGrid from 'components/reviews/ReviewGalleryMediaGrid';
import css from 'styles/containers/reviewGallery.scss';

// ReviewGallery is the modal popup accessible from PDP / Reviews when clicking on any review media.
export class ReviewGallery extends Component {
  handleReviewUpvoteClick = id => {
    const { product: { reviewData: { loadingReviews } }, upvoteReview, returnUrl } = this.props;
    if (!loadingReviews.includes(id)) {
      upvoteReview(id, returnUrl);
      trackEvent('TE_REVIEWS_RATEREVIEWHELPFUL');
    }
  };

  handleNextReviewClick = () => {
    const { navigateToReviewWithMedia, reviewGallery: { reviews, reviewId } } = this.props;
    const currentPageIndex = reviews.findIndex(review => review.id === reviewId);
    const nextReview = reviews[currentPageIndex + 1];

    if (currentPageIndex < reviews.length - 1 && nextReview) {
      navigateToReviewWithMedia(nextReview.id);
    }

    this.scrollReviewContentToTop();
  };

  handlePrevReviewClick = () => {
    const { navigateToReviewWithMedia, reviewGallery: { reviews, reviewId } } = this.props;
    const currentPageIndex = reviews.findIndex(review => review.id === reviewId);
    const prevReview = reviews[currentPageIndex - 1];

    if (currentPageIndex > 0 && prevReview) {
      navigateToReviewWithMedia(prevReview.id);
    }

    this.scrollReviewContentToTop();
  };

  handleSortReviewsClick = orderBy => {
    const { product: { detail: { productId } }, reviewGallery, sortMediaReviews, fetchProductReviewsWithMedia } = this.props;
    if (reviewGallery.orderBy === orderBy) {
      return;
    }
    sortMediaReviews(orderBy);
    fetchProductReviewsWithMedia(productId, false);
  };

  openMediaReview = (reviewId, mediaIndex) => {
    const { showReviewGalleryModal } = this.props;
    showReviewGalleryModal(reviewId, mediaIndex);
  };

  makeMediaReview = () => {
    const {
      context: { testId },
      props: {
        product: {
          detail: { brandName, productName },
          reviewData: { loadingReviews, submittedReviews }
        },
        reviewGallery: { reviews, reviewId, reviewMediaIndex }
      },
      handlePrevReviewClick, handleNextReviewClick, handleReviewUpvoteClick,
      openMediaReview
    } = this;

    const reviewIndex = reviews.findIndex(review => review.id === reviewId);
    const review = reviews[reviewIndex];

    return (
      <div className={css.reviewContentWrapper}>
        <div className={css.reviewContent} ref={el => this.reviewContentScrollBox = el}>
          <div className={css.mobileScrollBox}>
            <div className={css.reviewImageWrapper}>
              <ReviewGalleryImage
                review={review}
                reviewMediaIndex={reviewMediaIndex}/>
            </div>
            <div className={css.reviewComponentWrapper}>
              <Review
                review={review}
                productName={`${brandName} ${productName}`}
                forceTabularRatings={true}
                hiddenMediaItemIndex={reviewMediaIndex}
                limitSummaryHeight={false}
                loadingReviews={loadingReviews}
                onReviewMediaClick={openMediaReview}
                onReviewUpvoteClick={handleReviewUpvoteClick}
                shouldRenderStructuredData={false}
                showFitSurvey={false}
                showMedia={true}
                submittedReviews={submittedReviews} />
            </div>
          </div>
          <div className={css.reviewPaginationContainer}>
            <div role="navigation" className={css.reviewPagination} aria-label="pagination">
              <button
                type="button"
                aria-label="previous page"
                className={css.previousReviewButton}
                onClick={handlePrevReviewClick}
                data-test-id={testId('prevReview')}
                disabled={reviewIndex === 0}
              />
              <span className={css.reviewPageIndicator}>{reviewIndex + 1}/{reviews.length} Reviews</span>
              <button
                type="button"
                aria-label="next page"
                className={css.nextReviewButton}
                onClick={handleNextReviewClick}
                data-test-id={testId('nextReview')}
                disabled={reviewIndex + 1 === reviews.length}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  makeGalleryGrid = () => {
    const { product, reviewGallery } = this.props;
    const { detail: { productId } } = product;
    const { reviews, viewMediaType, isLoading } = reviewGallery;

    return reviews.length && (
      <div className={css.mediaGridWrapper}>
        <ReviewGalleryMediaGrid
          compactThumbnails={true}
          productId={productId}
          reviews={reviews}
          showVideoThumbnails={true}
          viewMediaType={viewMediaType}
          isLoading={isLoading}
          onOpenMediaReview={this.openMediaReview}/>
      </div>
    );
  };

  scrollReviewContentToTop = () => {
    const { reviewContentScrollBox } = this;
    if (reviewContentScrollBox) {
      reviewContentScrollBox.scrollTop = 0;
    }
  };

  render() {
    const {
      context: { testId },
      props: {
        reviewGallery: { isModalOpen, reviewId },
        hideReviewGalleryModal
      },
      makeGalleryGrid, makeMediaReview
    } = this;
    const modalData = { 'test-id': testId('reviewGalleryModal') };
    return (
      <Modal
        data={modalData}
        isOpen={isModalOpen}
        onRequestClose={hideReviewGalleryModal}
        portalClassName={css.modalPortal}
        overlayClassName={css.modalOverlay}
        className={css.modalContent}
        data-test-id={testId('reviewGalleryModal')}
        contentLabel="ReviewGalleryModal"
        bodyOpenClassName={css.modalOpenBodyStyle}>
        <div className={css.title}>Image Gallery</div>
        <button
          type="button"
          className={css.close}
          onClick={hideReviewGalleryModal}
          data-test-id={testId('closeButton')}>
          <Clear className={css.closeIcon} size={27}/>
        </button>
        <hr className={css.divider}/>
        {reviewId ? makeMediaReview() : makeGalleryGrid()}
      </Modal>
    );
  }
}

ReviewGallery.propTypes = {
  params: PropTypes.object.isRequired,
  product: PropTypes.shape({
    detail: PropTypes.shape({
      brandName: PropTypes.string.isRequired,
      productId: PropTypes.string.isRequired,
      productName: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  returnUrl: PropTypes.string.isRequired
};

ReviewGallery.contextTypes = {
  testId: PropTypes.func
};

const mapStateToProps = state => {
  const { product, reviews: { reviewGallery } } = state;
  return { product, reviewGallery };
};

export default connect(mapStateToProps, {
  upvoteReview,
  hideReviewGalleryModal,
  selectGalleryMedia,
  navigateToReviewWithMedia,
  toggleShowGallery,
  showReviewGalleryModal,
  sortMediaReviews,
  fetchProductReviewsWithMedia,
  filterReviewsByMediaType
})(ReviewGallery);
